/* ============================================================
   assets/int-pdp-title-fit.js                INTENTION — 2026-09-03
   THE TITLE FITTER, AND NOTHING ELSE. Apparel PDP, mobile only.

   🚨 THIS FILE IS LOADED NON-DEFERRED, ON PURPOSE, AND IT IS THE ONLY
   NON-DEFERRED SCRIPT WE ADD TO THE PAGE.

   sections/product.liquid renders it IMMEDIATELY AFTER THE TITLE MARKUP, at
   roughly 42% of the byte stream. Everything else stays in the deferred
   assets/int-pdp-mobile.js, which cannot run until the whole 1.6MB document
   has been parsed.

   Why it had to be split out: the title used to be held at
   `visibility: hidden` until that deferred script had MOVED it above the
   image and SIZED it. The order layer in int-pdp-mobile-critical.liquid now
   does the moving in CSS at first paint, so all that is left is the sizing —
   and sizing it here means the headline appears at 42% instead of 100%.

   ⚑ "MOVE THE CALL, NOT THE CODE." There is exactly ONE implementation of the
   fitter and it is this one. int-pdp-mobile.js calls `window.__intFitTitle`
   rather than carrying a copy, because two copies of a measuring loop drift
   and then disagree about the same headline.

   ⚑ It runs at parse time, which is the whole point, so it must not assume
   anything below it exists. It touches the title wrapper and nothing else.
   ============================================================ */
(function () {
  'use strict';
  if (window.__intFitTitle) return;

  var MQ = '(max-width: 820px)';

  function fitTitle(wrap) {
    if (!wrap) return;
    var h1 = wrap.querySelector('h1.product__title');
    if (!h1) return;
    /* ⚑ THE FLOOR IS 22, NOT 15 — 2026-09-03. Nils asked whether the longest
       titles fit on one line, and whether they should instead break between
       the Word of Intention and the Photo Art title.

       Measured: the longest name in the catalogue is
       "Gratitude - Bubbles and Sky Sticks" at 36 characters, and on a 390px
       phone it lands on ONE line at 26px inside a 316px column. So nothing
       in the catalogue needs to wrap today.

       The floor still mattered, though: at 15px a longer name would have
       kept shrinking until the headline stopped being a headline. 22px is
       where it stops looking like one, so the fitter now gives up there and
       `int-mob-title-wrap` takes over — and that class breaks the line at
       the seam between the Word and the Photo Art title, which is where Nils
       asked for it, not at whatever word happens to fall at the edge. */
    var MAX = 32, MIN = 22;
    /* cache for the skip-a-pass check below, per wrapper */
    var lastAvail = null, lastPx = null, lastW = null, lastTooWide = false;
    function apply(px) { wrap.style.setProperty('--int-mob-title', px + 'px'); }
    function run() {
      var main = h1.querySelector('.title-main');
      if (!main) return;
      wrap.classList.remove('int-mob-title-wrap');
      /* Measure the space against the WRAPPER's content box, not the h1.
         The h1 does not shrink to its parent here (it reported 352px inside
         a 303px column), so trusting it let the line run off the screen. */
      var wcs = getComputedStyle(wrap);
      var avail = wrap.clientWidth
                - parseFloat(wcs.paddingLeft || 0)
                - parseFloat(wcs.paddingRight || 0)
                - 2;
      if (avail <= 0) return;
      /* Measure on an off screen clone at width:max-content. A clipped or
         clamped element reports the column width, not the text width, and
         the loop then stops one step too early and the line is cut off. */
      var probe = main.cloneNode(true);
      probe.style.cssText = 'position:absolute;left:-99999px;top:0;width:max-content;max-width:none;white-space:nowrap;visibility:hidden;pointer-events:none;';
      probe.removeAttribute('id');
      h1.appendChild(probe);
      function textW() {
        return Math.max(probe.getBoundingClientRect().width, probe.scrollWidth);
      }
      /* ⚑ 2026-09-17: SKIP A PASS THAT CANNOT CHANGE THE ANSWER.

         The schedule below deliberately runs this four times, and the header
         explains why: fonts.ready is not a promise that font loading is over,
         so a later pass can legitimately land on a different size. But when
         neither the available width NOR the measured text width has moved
         since the last pass, the search can only return the number it
         returned last time. One probe instead of five.

         ⚑ This does NOT shorten the schedule. Every pass still runs and still
         measures; it just stops re-deriving an answer it already has. The
         moment a webfont swaps, the width changes and the full search runs.
         ⚑ REVERT: delete this block and the four `last*` variables. */
      if (lastPx !== null && lastAvail === avail) {
        apply(lastPx);
        if (Math.round(textW() * 2) === Math.round(lastW * 2)) {
          h1.removeChild(probe);
          if (lastTooWide) wrap.classList.add('int-mob-title-wrap');
          return;
        }
      }
      /* ⚑ 2026-09-17: BINARY SEARCH, NOT A WALK DOWN. Same answer, a third of
         the layout reads.

         Every `apply()` writes a CSS variable and every `textW()` reads a box,
         so each step of the old loop forced a synchronous layout of a 1.8MB
         document. From 32 to 22 that is up to ELEVEN forced layouts per pass,
         and this fitter deliberately runs four times (see the schedule below),
         so up to forty four.

         Measured before this change, CPU profile at 100us on the apparel PDP
         at 400px: `textW` 127ms self time and `run` 61ms, 210ms for the file,
         the third largest script on the page.

         Width is monotonic in font size for the same string, so "does it fit"
         is a monotone predicate and a binary search over 22..32 returns
         exactly the size the downward walk returned, in at most four probes
         instead of ten. The MAX fast path is kept because most titles fit at
         32 and cost one probe either way.
         ⚑ REVERT: restore the while loop, it is four lines. */
      var px = MAX, wAtPx;
      apply(px);
      wAtPx = textW();
      var fits = wAtPx <= avail;
      if (!fits) {
        var lo = MIN, hi = MAX - 1, best = -1, bestW = null;
        while (lo <= hi) {
          var mid = (lo + hi) >> 1;
          apply(mid);
          var wm = textW();
          if (wm <= avail) { best = mid; bestW = wm; lo = mid + 1; }
          else { hi = mid - 1; }
        }
        if (best < 0) { px = MIN; apply(px); wAtPx = textW(); fits = false; }
        else { px = best; apply(px); wAtPx = bestW; fits = true; }
      }
      var tooWide = !fits;
      lastAvail = avail; lastPx = px; lastW = wAtPx; lastTooWide = tooWide;
      h1.removeChild(probe);
      /* Still too wide at the floor: let it wrap instead of clipping. */
      if (tooWide) wrap.classList.add('int-mob-title-wrap');
    }
    /* ⚑ Run it FEW times, but run it until the metrics stop moving. Every
       run can land on a different size and each one is a visible resize of
       a 30px headline, so the list below is deliberately short — but it is
       longer than it once was, and here is why.

       `document.fonts.ready` is NOT a promise that the page has finished
       loading fonts. It resolves when the fonts pending AT THAT MOMENT are
       done, and third parties on this page (Weglot, Easysize) start their
       own loads afterwards, so the document goes back to
       `document.fonts.status === "loading"` again. Measured on the apparel
       PDP at 410px: the fonts.ready pass settled on 29px, the real webfont
       arrived after it, and the headline then rendered 345px wide inside a
       336px column — about 7px off the right edge. Re-running the very same
       fitter by hand immediately returned 28px, which fits at 335px.

       So: now, when fonts.ready first resolves, again at window load, and
       once more shortly after load. Each pass is idempotent — if nothing
       has changed it recomputes the same number and nothing moves — so the
       extra passes are invisible except in exactly the case they exist for.

       ⚑ Do not delete the post-load pass on the grounds that the deferred
       int-pdp-mobile.js also calls the fitter. It does, but the whole point
       of this file is that the title is correct at 42% of the byte stream,
       without waiting for that script. */
    run();
    if (document.fonts && document.fonts.ready && document.fonts.ready.then) {
      document.fonts.ready.then(run);
    }
    if (document.readyState === 'complete') {
      setTimeout(run, 0);
      setTimeout(run, 800);
    } else {
      window.addEventListener('load', function () {
        run();
        setTimeout(run, 800);
      });
    }
    window.addEventListener('orientationchange', function () { setTimeout(run, 200); });
  }

  window.__intFitTitle = fitTitle;

  /* Run now, on the title that is already in the document above this script.
     ⚑ The class is added even when the fitter finds nothing to do. It is a
     RELEASE, not a report: the critical CSS holds the title invisible until
     it lands, so failing to add it would leave the headline hidden for good.
     Fail visible. */
  function go() {
    try {
      if (window.matchMedia && window.matchMedia(MQ).matches) {
        fitTitle(document.querySelector('.product__block_title .product__title__wrapper'));
      }
    } catch (e) {}
    if (document.body) document.body.classList.add('int-mob-title-ready');
  }
  go();
  /* One more pass once the webfont resolves. fitTitle already hooks
     fonts.ready itself; this only covers the case where the body element did
     not exist yet at parse time, which cannot happen from where this is
     rendered but costs nothing to be sure of. */
  if (!document.body) {
    document.addEventListener('DOMContentLoaded', function () {
      document.body.classList.add('int-mob-title-ready');
    });
  }
})();
